/**
 * SaveReportModal — modal for saving or updating a report.
 *
 * Captures report name and optional description, then persists
 * the current section configuration to Firestore.
 */
import { useState, useCallback, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { cn } from '@/lib/cn';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';

interface Report {
  id: string;
  name: string;
  description?: string;
  sectionId: string;
  config: Record<string, unknown>;
  slug?: string;
}

interface SaveReportModalProps {
  /** Current section state to save as config */
  config: Record<string, unknown>;
  /** Section ID for scoping reports (defaults to 'product') */
  sectionId?: string;
  /** Existing report ID if updating */
  existingReportId?: string;
  /** Pre-fill name when updating */
  existingName?: string;
  /** Pre-fill description when updating */
  existingDescription?: string;
  onClose: () => void;
  onSaved: (report: Report) => void;
}

export function SaveReportModal({
  config,
  sectionId = 'product',
  existingReportId,
  existingName = '',
  existingDescription = '',
  onClose,
  onSaved,
}: SaveReportModalProps) {
  const [name, setName] = useState(existingName);
  const [description, setDescription] = useState(existingDescription);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isUpdate = !!existingReportId;
  const canSave = name.trim().length > 0 && !saving;

  // Escape key to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!canSave) return;

    setSaving(true);
    setError('');

    try {
      let result: { report: Report };

      if (isUpdate) {
        result = await api.patch<{ report: Report }>(
          endpoints.reports.update(existingReportId!, sectionId),
          {
            name: name.trim(),
            description: description.trim() || undefined,
            config,
          },
        );
      } else {
        result = await api.post<{ report: Report }>(
          endpoints.reports.create(),
          {
            sectionId,
            name: name.trim(),
            description: description.trim() || undefined,
            config,
          },
        );
      }

      onSaved(result.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save report');
    } finally {
      setSaving(false);
    }
  }, [canSave, isUpdate, existingReportId, name, description, config, onSaved]);

  // Enter key to save (when not in textarea)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && canSave) {
        e.preventDefault();
        handleSave();
      }
    },
    [canSave, handleSave],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {isUpdate ? 'Update Report' : 'Save Report'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4" onKeyDown={handleKeyDown}>
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Report Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Daily Purchase Analysis"
              className={cn(
                'w-full h-9 rounded-lg border bg-white px-3 text-sm text-slate-900',
                'transition-colors duration-150',
                'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
                'border-slate-200 hover:border-slate-300',
                'placeholder:text-slate-400',
              )}
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Description <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what this report tracks..."
              rows={3}
              className={cn(
                'w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 resize-none',
                'transition-colors duration-150',
                'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
                'border-slate-200 hover:border-slate-300',
                'placeholder:text-slate-400',
              )}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/50 rounded-b-xl">
          <button
            onClick={onClose}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium',
              'text-slate-700 hover:bg-slate-100 transition-colors',
            )}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
              'bg-brand-600 text-white hover:bg-brand-700 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : isUpdate ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
